import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Calendar,
  MapPin,
  Package,
  Ship,
  UserRound,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CarrierLogo } from "@/components/common/CarrierLogo";
import { SensitiveText } from "@/components/common/Sensitive";
import { cn } from "@/lib/utils";
import { EVENT_KIND_LABELS } from "./scheduleConstants";
import type { ScheduleEvent, ScheduleEventOriginGroup } from "@/types/schedule";
import { EVENT_STYLES } from "./ScheduleLegend";

interface ScheduleEventDetailProps {
  event: ScheduleEvent | null;
  /** Nested in the schedule page split pane (no second glass card). */
  embedded?: boolean;
}

function bookingHref(id: string): string {
  return `/bookings/${encodeURIComponent(id)}`;
}

function originCountLabel(groups: ScheduleEventOriginGroup[]): string | null {
  if (groups.length <= 1) return null;
  return `${groups.length} origins`;
}

function StuffingGroups({ groups }: { groups: ScheduleEventOriginGroup[] }) {
  return (
    <div className="flex flex-col gap-2.5">
      {groups.map((origin) => (
        <section
          key={origin.locode || origin.origin}
          className="overflow-hidden rounded-lg border border-border/50 bg-background/30"
        >
          <header className="flex items-center justify-between gap-2 px-3 py-2">
            <div className="flex min-w-0 items-center gap-1.5">
              <MapPin className="size-3.5 shrink-0 text-muted-foreground" />
              <h3 className="truncate text-sm font-medium">{origin.origin}</h3>
            </div>
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {origin.bookingCount}{" "}
              {origin.bookingCount === 1 ? "booking" : "bookings"}
            </span>
          </header>
          <div className="flex flex-col gap-2.5 border-t border-border/40 px-3 py-2">
            {origin.shippers.map((shipper) => (
              <div key={shipper.shipper} className="min-w-0">
                <p className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <SensitiveText
                    value={shipper.shipper}
                    className="truncate font-medium text-foreground/80"
                  />
                  {shipper.bookings.length > 1 && (
                    <span className="shrink-0 tabular-nums">
                      {shipper.bookings.length}
                    </span>
                  )}
                </p>
                <ul className="flex flex-col gap-0.5">
                  {shipper.bookings.map((booking) => (
                    <li key={booking.id}>
                      <Link
                        to={bookingHref(booking.id)}
                        className="flex items-center justify-between gap-2 rounded-md px-2 py-1 text-xs transition-colors hover:bg-muted/60"
                      >
                        <span className="font-medium tabular-nums">
                          {booking.id}
                        </span>
                        <span className="min-w-0 truncate text-muted-foreground">
                          {booking.destination}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function ScheduleEventDetail({
  event,
  embedded = false,
}: ScheduleEventDetailProps) {
  const bookingIds = event?.bookingIds ?? (event?.bookingId ? [event.bookingId] : []);
  const isGroup = bookingIds.length > 1;
  const stuffingGroups = event?.groups?.length ? event.groups : null;
  const originsLabel = stuffingGroups ? originCountLabel(stuffingGroups) : null;

  const panelClass = cn(
    "flex min-h-[12rem] flex-col gap-4 overflow-y-auto p-4 lg:p-5",
    embedded
      ? "h-full max-h-full min-h-0 bg-transparent shadow-none"
      : "glass-panel max-h-[calc(var(--app-vvh,var(--app-vh,100vh))-var(--main-sticky-top)-2rem)]",
  );

  if (!event) {
    return (
      <div
        className={cn(
          panelClass,
          "items-center justify-center p-6 text-center",
        )}
      >
        <Calendar className="mb-2 size-8 text-muted-foreground/50" />
        <p className="text-sm font-medium">Select an event</p>
        <p className="mt-1 max-w-[16rem] text-xs text-muted-foreground">
          Click a block on the calendar to see lane, carrier, and booking details.
        </p>
      </div>
    );
  }

  return (
    <div className={panelClass}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <Badge variant="outline" className={EVENT_STYLES[event.kind]}>
            {EVENT_KIND_LABELS[event.kind]}
          </Badge>
          <h2 className="mt-2 text-base font-semibold leading-snug">{event.title}</h2>
          <p className="text-sm text-muted-foreground">
            {event.time}
            {originsLabel && ` · ${originsLabel}`}
            {isGroup && ` · ${bookingIds.length} bookings`}
          </p>
        </div>
      </div>

      <dl className="grid gap-3 text-sm">
        <div className="flex items-start gap-2">
          <Ship className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div>
            <dt className="text-xs text-muted-foreground">Carrier</dt>
            <dd className="font-medium">
              <CarrierLogo carrier={event.carrier} size={16} />
            </dd>
          </div>
        </div>
        {!stuffingGroups && (
          <div className="flex items-start gap-2">
            <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div>
              <dt className="text-xs text-muted-foreground">Lane</dt>
              <dd className="font-medium">{event.lane}</dd>
            </div>
          </div>
        )}
        {event.vessel && (
          <div className="flex items-start gap-2">
            <Ship className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div>
              <dt className="text-xs text-muted-foreground">Vessel</dt>
              <dd className="font-medium">{event.vessel}</dd>
            </div>
          </div>
        )}
        {event.agent && (
          <div className="flex items-start gap-2">
            <UserRound className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div>
              <dt className="text-xs text-muted-foreground">Agent</dt>
              <dd className="font-medium">{event.agent}</dd>
            </div>
          </div>
        )}
        {!stuffingGroups && bookingIds.length > 0 && (
          <div className="flex items-start gap-2">
            <Package className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <dt className="text-xs text-muted-foreground">
                {isGroup ? `Bookings (${bookingIds.length})` : "Booking"}
              </dt>
              {isGroup ? (
                <dd className="mt-1 flex flex-wrap gap-1.5">
                  {bookingIds.map((id) => (
                    <Link
                      key={id}
                      to={bookingHref(id)}
                      className="rounded-md border border-border/60 bg-background/40 px-2 py-0.5 text-xs font-medium tabular-nums transition-colors hover:bg-muted/60"
                    >
                      {id}
                    </Link>
                  ))}
                </dd>
              ) : (
                <dd className="font-medium tabular-nums">{bookingIds[0]}</dd>
              )}
            </div>
          </div>
        )}
      </dl>

      {stuffingGroups && <StuffingGroups groups={stuffingGroups} />}

      {!isGroup && bookingIds[0] && (
        <Button asChild variant="outline" className="w-full gap-2">
          <Link to={bookingHref(bookingIds[0])}>
            View booking
            <ArrowUpRight className="size-4" />
          </Link>
        </Button>
      )}
    </div>
  );
}
